import { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import ContactModal from "@/components/ContactModal";
import { Twitter, Instagram } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Footer() {
  const [contactOpen, setContactOpen] = useState(false);
  const { user, logout } = useAuth();

  return (
    <footer className="border-t border-white/5 bg-[#070706]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid gap-8 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <div className="mb-4">
            <Logo small />
          </div>
          <p className="text-sm text-slate-500 leading-relaxed max-w-sm">
            The discovery leaderboard for startups and businesses. Rank Position shows visibility.
            HighRank Score shows reputation. Two signals — never blended, never for sale as one.
          </p>
          <p className="mt-4 text-xs text-slate-600 leading-relaxed max-w-sm">
            HighRank Score is HighRank's own platform score — not a Google rating and not a guarantee of
            quality. Unclaimed listings were published by HighRank and imply no endorsement or partnership.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <a
              href="https://x.com/highrankspace"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-x-link"
              className="flex items-center justify-center w-8 h-8 rounded-full border border-white/10 bg-white/5 text-slate-400 hover:text-slate-100 hover:border-white/25 transition-all"
            >
              <Twitter size={14} />
            </a>
            <a
              href="https://instagram.com/highrank.space"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="flex items-center justify-center w-8 h-8 rounded-full border border-white/10 bg-white/5 text-slate-400 hover:text-slate-100 hover:border-white/25 transition-all"
            >
              <Instagram size={14} />
            </a>
          </div>
        </div>
        <div>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-4">Platform</p>
          <ul className="space-y-2.5 text-sm">
            <li>
              <Link to="/leaderboard" data-testid="footer-leaderboard-link" className="text-slate-400 hover:text-emerald-400 transition-colors">
                Leaderboard
              </Link>
            </li>
            <li>
              <Link to="/discover" data-testid="footer-discover-link" className="text-slate-400 hover:text-emerald-400 transition-colors">
                Discover
              </Link>
            </li>
            <li>
              <Link to="/pricing" data-testid="footer-pricing-link" className="text-slate-400 hover:text-emerald-400 transition-colors">
                Pricing
              </Link>
            </li>
            {!user && (
              <li>
                <Link to="/add-business" data-testid="footer-add-business-link" className="text-slate-400 hover:text-emerald-400 transition-colors">
                  List your business
                </Link>
              </li>
            )}
            {!user ? (
              <li>
                <Link to="/auth" data-testid="footer-signin-link" className="text-slate-400 hover:text-emerald-400 transition-colors">
                  Sign in
                </Link>
              </li>
            ) : (
              <li>
                <button
                  data-testid="footer-signout-btn"
                  onClick={logout}
                  className="text-slate-400 hover:text-emerald-400 transition-colors text-left"
                >
                  Sign out
                </button>
              </li>
            )}
          </ul>
        </div>
        <div>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-4">Regions</p>
          <ul className="space-y-2.5 text-sm text-slate-400">
            <li>
              <Link to="/leaderboard/us" data-testid="footer-region-us-link" className="hover:text-emerald-400 transition-colors">
                United States
              </Link>
            </li>
            <li>
              <Link to="/leaderboard/uk" data-testid="footer-region-uk-link" className="hover:text-emerald-400 transition-colors">
                United Kingdom
              </Link>
            </li>
            <li>
              <Link to="/leaderboard/india" data-testid="footer-region-india-link" className="hover:text-emerald-400 transition-colors">
                India
              </Link>
            </li>
            <li>
              <Link to="/leaderboard/global" data-testid="footer-region-global-link" className="hover:text-emerald-400 transition-colors">
                Global
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-4">Legal</p>
          <ul className="space-y-2.5 text-sm">
            <li>
              <Link to="/privacy" data-testid="footer-privacy-link" className="text-slate-400 hover:text-emerald-400 transition-colors">
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link to="/terms" data-testid="footer-terms-link" className="text-slate-400 hover:text-emerald-400 transition-colors">
                Terms of Service
              </Link>
            </li>
            <li>
              <Link to="/refund-policy" data-testid="footer-refund-policy-link" className="text-slate-400 hover:text-emerald-400 transition-colors">
                Refund Policy
              </Link>
            </li>
            <li>
              <button
                data-testid="footer-contact-btn"
                onClick={() => setContactOpen(true)}
                className="text-slate-400 hover:text-emerald-400 transition-colors text-left"
              >
                Contact Us
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="font-mono2 text-[11px] text-slate-600">© 2026 HIGHRANK.SPACE — GET DISCOVERED. STAY RANKED.</p>
          <p className="font-mono2 text-[11px] text-slate-600">BOOST YOUR LISTING</p>
        </div>
      </div>
      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
    </footer>
  );
}
